<script>
import DiagnosisSelect from "@/components/ui/DiagnosisSelect";
import FileUpload from "@/components/ui/FileUpload";
import { isIndicationsEnabled } from "@/services/config";
import { mapGetters } from "vuex";
import fileUploadConfigService from "@/services/fileupload-config";

export default {
  name: "FormRenderer",
  components: {
    DiagnosisSelect,
    FileUpload,
  },
  props: {
    selectedForm: { type: Object, default: null },
    patient: { type: Object, required: false },
    isVisible: { type: Boolean, default: false },
    prefillData: { type: Object, default: null },
    isEditing: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
  },

  data() {
    return {
      isPosting: false,
      isIndicationsEnabled,
      hasFiles: false,
    };
  },

  computed: {
    ...mapGetters(["user"]),

    uploadMediaEndpoint() {
      return fileUploadConfigService.getFormFilesEndpoint("placeholder");
    },
  },

  watch: {
    prefillData: {
      handler(newPrefillData) {
        if (newPrefillData && this.selectedForm) {
          this.applyPrefillData();
        }
      },
      immediate: true,
    },
  },

  methods: {
    getDropdownOptions() {
      return {
        method: "put",
        uploadMultiple: true,
        maxFiles: 3,
        maxFilesize: 5,
        acceptedFiles: "image/*,application/pdf",
      };
    },

    onFileAdded() {
      this.hasFiles = true;
    },

    onFilesRemoved() {
      this.hasFiles = false;
    },

    onFilesUpdated(files) {
      this.hasFiles = files && files.length > 0;
    },

    getDropzoneFiles() {
      if (this.$refs.fileUpload && this.$refs.fileUpload.$refs.uploader) {
        const dropzone = this.$refs.fileUpload.$refs.uploader.dropzone;
        return dropzone.files.filter((f) => !f.key);
      }
      return [];
    },

    async saveForm() {
      if (!this.selectedForm || !this.patient) return;

      this.isPosting = true;

      const dropzoneFiles = this.hasFiles ? this.getDropzoneFiles() : [];

      try {
        if (this.isEditing && this.prefillData) {
          await this.$confirm(
            "¿Está seguro que desea guardar los cambios realizados en el formulario?",
            "Confirmar cambios",
            {
              confirmButtonText: "Guardar",
              cancelButtonText: "Cancelar",
              type: "question",
            }
          );

          this.handleUpdatedForm(dropzoneFiles);
        } else {
          this.handleCompletedForm(dropzoneFiles);
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error(
            "Error al guardar formulario desde FormRenderer:",
            error
          );
        }
      } finally {
        this.isPosting = false;
      }
    },

    handleUpdatedForm(dropzoneFiles) {
      const updatedForm = {
        ...this.prefillData,
        ...this.selectedForm,
        _id: this.prefillData._id,
        createdAt: this.prefillData.createdAt,
        isSent: false,
        hasFiles: this.hasFiles,
        dropzoneFiles,
      };

      this.$emit("form-updated", {
        ...updatedForm,
        name: this.selectedForm.name,
        form: {
          _id: this.selectedForm._id,
          name: this.selectedForm.name,
        },
        createdBy: this.user,
      });

      this.$bus.$emit("form-updated", {
        ...updatedForm,
        name: this.selectedForm.name,
        form: {
          _id: this.selectedForm._id,
          name: this.selectedForm.name,
        },
        createdBy: this.user,
      });

      this.$message.success("Formulario actualizado exitosamente");
    },

    handleCompletedForm(dropzoneFiles) {
      const completedForm = {
        ...this.selectedForm,
        form: this.selectedForm._id,
        patient: this.patient._id,
        createdBy: this.user,
        createdAt: new Date().toISOString(),
        isSent: false,
        hasFiles: this.hasFiles,
        dropzoneFiles,
      };

      this.$emit("form-completed", {
        ...completedForm,
        name: this.selectedForm.name,
        form: {
          _id: this.selectedForm._id,
          name: this.selectedForm.name,
        },
        createdBy: this.user,
      });

      this.$bus.$emit("form-completed", {
        ...completedForm,
        name: this.selectedForm.name,
        form: {
          _id: this.selectedForm._id,
          name: this.selectedForm.name,
        },
        createdBy: this.user,
      });

      this.$message.success("Formulario agregado exitosamente");
    },

    cancelForm() {
      this.$emit("cancel-form");
    },

    applyPrefillData() {
      if (!this.prefillData || !this.selectedForm) return;

      if (this.prefillData.sections && this.selectedForm.sections) {
        this.selectedForm.sections.forEach((section, sectionIndex) => {
          const prefillSection = this.prefillData.sections[sectionIndex];
          if (!prefillSection) return;

          section.questions.forEach((question, questionIndex) => {
            const prefillQuestion =
              prefillSection.questions?.[questionIndex] || null;
            if (!prefillQuestion) return;

            this.$set(question, "selected", prefillQuestion.selected);
            this.$set(question, "observation", prefillQuestion.observation);
          });
        });
      }
    },

    calculateQualifierMeasurement(section) {
      const qualification = section.questions.reduce((sum, question) => {
        const answerValue = question.answers.find(
          (answer) => answer.value === question.selected
        );
        return sum + (answerValue ? answerValue.qualifierValue : 0);
      }, 0);

      let qualificationMeasurement = {};

      section.qualifier.measurement.forEach((elem) => {
        if (qualification >= elem.min && qualification <= elem.max) {
          qualificationMeasurement = elem;
        }
      });

      return {
        ...qualificationMeasurement,
        qualification,
        target: section.qualifier.target,
      };
    },

    getQualifierName(section) {
      const { name, qualification, target } =
        this.calculateQualifierMeasurement(section);

      return `${name} (${qualification}/${target})`;
    },

    getQualifierStyle(section) {
      const { style } = this.calculateQualifierMeasurement(section);
      return style && JSON.parse(JSON.stringify(style));
    },

    // Slider expects numeric values, these 2 functions are used to convert between the label (string value) and the value (numeric value)
    // value (numeric value) => label (string value)
    getSliderLabel(question, value) {
      const step = 100 / (question.answers.length - 1);
      const index = Math.round(value / step);
      return question.answers[index].value;
    },

    // label (string value) => value (numeric value)
    getSliderValue(question, value) {
      const answer = question.answers.find((answer) => answer.value === value);
      const index = question.answers.indexOf(answer);
      const step = 100 / (question.answers.length - 1);
      return index * step;
    },

    getSectionStyle(section) {
      return section.style?.direction === "row"
        ? {
            display: "grid",
            gridTemplateColumns: section.style?.columns
              ? `repeat(${section.style.columns}, 1fr)`
              : "repeat(2, 1fr)",
          }
        : { display: "flex", flexDirection: "column" };
    },
  },
};
</script>

<template lang="pug">
.form-container(v-if="isVisible && selectedForm")
  .form-actions(v-if="!readonly")
    el-button.bottom(@click="cancelForm" size="small") Cancelar
    el-button.bottom(
      type="primary"
      @click="saveForm"
      :loading="isPosting"
      size="small"
    ) {{ isEditing ? 'Actualizar' : 'Agregar' }}
  .form-header(v-if="!hideHeader")
    h3.form-title {{ selectedForm.name || selectedForm.title || selectedForm.description }}
  

  .form-content
    .form-section(v-for="section in selectedForm.sections" :key="section.name")
      .section-header
        h4.section-title {{ section.name }}
        .qualifier-info(v-if="section.qualifier")
          span.qualifier-name {{ section.qualifier.name }}: 
          span.qualifier-value(:style="getQualifierStyle(section)") {{ getQualifierName(section) }}

      .form-questions(:style="getSectionStyle(section)")
        .question-item(v-for="question in section.questions" :key="question.name")
          // Select con opciones
          .question-field(v-if="question.type === 'select' && !question.action")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-select(v-model="question.selected" placeholder="Seleccione una opción" :disabled="readonly")
                el-option(
                  v-for="answer in question.answers"
                  :value="answer.value"
                  :key="answer.value"
                  :label="answer.value"
                )
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Diagnosis Select
          .question-field(v-if="question.action && question.action.type === 'indications' && question.action.subtype === 'diagnosis' && isIndicationsEnabled")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              DiagnosisSelect(
                :value="question.selected"
                @input="diagnosis => question.selected = diagnosis"
                :disabled="readonly"
              )
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Input de texto
          .question-field(v-if="question.type === 'input'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-horizontal' ")
              label.question-label {{ question.name }}
              el-input(v-model="question.selected" placeholder="Ingrese su respuesta" :disabled="readonly")
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")
          
          // Input de fecha
          .question-field(v-if="question.type === 'date' || question.type === 'datetime'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-date-picker(v-model="question.selected" placeholder="Ingrese su respuesta" :disabled="readonly" :type="question.type === 'date' ? 'date' : 'datetime'")
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Input numérico
          .question-field(v-if="question.type === 'inputNumber'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-input(v-model="question.selected" type="number" step=".01" placeholder="Ingrese un número" :disabled="readonly")
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Checkbox
          .question-field(v-if="question.type === 'checkbox'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-checkbox-group.checkbox-list(v-model="question.selected" :disabled="readonly")
                el-checkbox(
                  v-for="answer in question.answers"
                  :value="answer.value"
                  :key="answer.value"
                  :label="answer.value"
                  :disabled="readonly"
                )
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Radio buttons
          .question-field(v-if="question.type === 'radio'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-radio-group(v-model="question.selected" :disabled="readonly")
                el-radio(
                  v-for="answer in question.answers"
                  :key="answer.value"
                  :label="answer.value"
                  :class="answer.style && answer.style.color ? `border-${answer.style.color}` : ''",
                  :disabled="readonly"
                )
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(
                :disabled="question.selected === 'No' || readonly"
                v-model="question.observation"
                placeholder="Observaciones adicionales"
              )
          
          // Slider
          .question-field(v-if="question.type === 'slider'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-slider(
                v-if="!readonly"
                :value="getSliderValue(question, question.selected)"
                @input="value => question.selected = getSliderLabel(question, value)"
                :step="100 / (question.answers.length - 1)"
                show-tooltip="true"
                :format-tooltip="(value) => getSliderLabel(question, value)"
                style="min-width: 50%; margin: 10px;"
              )
              span(v-else) {{ question.selected }}
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")

          // Textarea
          .question-field(v-if="question.type === 'textarea'")
            .question(:class="(question.style && question.style.direction === 'row') ? 'question-horizontal' : 'question-vertical' ")
              label.question-label {{ question.name }}
              el-input(
                v-model="question.selected"
                type="textarea"
                :placeholder="`${question.name}...`"
                :rows="3"
                :disabled="readonly"
              )
            .observation-field(v-if="question.observationEnable")
              label.observation-label Especifique
              el-input(v-model="question.observation" placeholder="Observaciones adicionales" :disabled="readonly")
    .form-file-section(v-if="!readonly")
      file-upload(
        ref="fileUpload"
        mode="list"
        :url="uploadMediaEndpoint"
        :dropzone-options="getDropdownOptions()"
        :files="attachedFiles"
        @update:files="onFilesUpdated"
        @file-added="onFileAdded"
        @files-removed="onFilesRemoved"
      )
</template>

<style lang="scss">
.form-container {
  margin-top: 20px;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  width: 100%;
  background-color: #ffffff;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e4e7ed;
}

.form-title {
  margin: 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.form-file-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.file-label {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
  margin-bottom: 10px;
  display: block;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;

  .bottom {
    border-radius: 10px;
  }
}

.form-content {
  max-height: 500px;
  overflow-y: auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.form-section {
  margin-bottom: 25px;
}

.section-header {
  margin-bottom: 15px;
}

.section-title {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 16px;
  font-weight: 600;
}

.qualifier-info {
  font-size: 14px;
}

.qualifier-name {
  color: #909399;
}

.qualifier-value {
  font-weight: 600;
}

.form-questions {
  gap: 8px;
}

.question-item {
  background-color: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.question-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.question-horizontal {
  display: flex;
  gap: 8px;
  flex-direction: row;
  align-items: center;
}

.question-vertical {
  display: flex;
  gap: 8px;
  flex-direction: column;
}

.question-label {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
  line-height: 1.5;
}

.observation-field {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.observation-label {
  font-weight: 500;
  color: #606266;
  font-size: 13px;
  margin-bottom: 5px;
  display: block;
}

.form-container {
  .el-radio {
    width: 100%;

    &.border-green {
      .el-radio__input .el-radio__inner {
        border: 1px solid #42b983;
      }
      .el-radio__input.is-checked .el-radio__inner {
        background-color: #42b983;
      }
    }

    &.border-yellow {
      .el-radio__input .el-radio__inner {
        border: 1px solid #e3ac05;
      }
      .el-radio__input.is-checked .el-radio__inner {
        background-color: #e3ac05;
      }
    }

    &.border-red {
      .el-radio__input .el-radio__inner {
        border: 1px solid #eb5757;
      }
      .el-radio__input.is-checked .el-radio__inner {
        background-color: #eb5757;
      }
    }
  }

  .checkbox-list {
    display: flex;
    flex-direction: column;

    .el-checkbox {
      width: 100%;
      margin-bottom: 0px;
    }
  }
}

.form-container .el-select,
.form-container .el-input,
.form-container .el-textarea,
.form-container .el-checkbox-group,
.form-container .el-radio-group {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.form-container .el-select .el-input,
.form-container .el-input .el-input__inner,
.form-container .el-textarea .el-textarea__inner {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.form-container .el-radio,
.form-container .el-checkbox {
  white-space: normal;
  word-wrap: break-word;
  max-width: 100%;
}

.form-container .el-radio__label,
.form-container .el-checkbox__label {
  white-space: normal;
  word-wrap: break-word;
  max-width: calc(100% - 20px);
}
</style>
